/**
 * Account Analytics — Lightweight Mixpanel event tracking for account pages.
 *
 * Unlike kiosk-analytics (which auto-tracks XState machine transitions),
 * account pages use manual tracking: import and call at each interaction point.
 *
 * Event format: interaction:account:{group}:{event}
 * Examples:
 *   - interaction:account:auth:dob_verified
 *   - interaction:account:payment:success
 *   - interaction:account:nav:tab_selected
 *
 * Session ID + userId + orgId are stored in sessionStorage and auto-attached
 * to every event after initAccountSession() is called (on DOB verification).
 *
 * Usage:
 *   import { trackAccountEvent } from "@/app/directories/account/_analytics/account-analytics";
 *   trackAccountEvent("nav:tab_selected", { tab: "appointments" });
 */

import { trackEvent } from "@frontend/lib/analytics";

// ============================================================================
// Session Management (sessionStorage)
// ============================================================================

const SESSION_ID_KEY = "account_session_id";
const USER_ID_KEY = "account_user_id";
const ORG_ID_KEY = "account_org_id";
const ENTRY_SOURCE_KEY = "account_entry_source";
const ENTRY_SOURCE_TYPE_KEY = "account_entry_source_type";

function getSessionValue(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Initialize an account analytics session. Call once on DOB verification success.
 * Stores session ID, userId, and orgId in sessionStorage for all subsequent events.
 */
export function initAccountSession(userId: string, orgId: number): string {
  const sessionId = crypto.randomUUID();
  try {
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
    sessionStorage.setItem(USER_ID_KEY, userId);
    sessionStorage.setItem(ORG_ID_KEY, String(orgId));
  } catch {
    // SSR or sessionStorage unavailable — session tracking degrades gracefully
  }
  return sessionId;
}

/**
 * Capture the entry source (e.g. secure-link code) for this session. First
 * write wins — once a source is set, later calls are no-ops. This protects
 * against a stray `?source=` showing up on an internal navigation overwriting
 * the real entry source that brought the patient in.
 */
export function initAccountEntrySource(
  source: string,
  sourceType: string,
): void {
  try {
    if (sessionStorage.getItem(ENTRY_SOURCE_KEY)) return;
    sessionStorage.setItem(ENTRY_SOURCE_KEY, source);
    sessionStorage.setItem(ENTRY_SOURCE_TYPE_KEY, sourceType);
  } catch {
    // SSR or sessionStorage unavailable — entry-source attribution degrades gracefully
  }
}

// ============================================================================
// PII Anonymization
// ============================================================================

const PII_FIELDS = new Set([
  "firstName",
  "firstname",
  "lastName",
  "lastname",
  "dateOfBirth",
  "email",
  "phone",
  "name",
]);

/**
 * Redact PII fields in event metadata. Exported so other account trackers
 * (e.g. the super-checkout telemetry bridge) can scrub props the same way.
 */
export function anonymizeAccountData(
  data: Record<string, unknown>,
): Record<string, unknown> {
  const anonymized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    if (PII_FIELDS.has(key) && value !== undefined && value !== null) {
      anonymized[key] = "[REDACTED]";
    } else {
      anonymized[key] = value;
    }
  }
  return anonymized;
}

// ============================================================================
// Event Tracking
// ============================================================================

/**
 * The session/attribution block every account event carries: session ID,
 * userId, orgId, entry source (all from sessionStorage — null until
 * initAccountSession/initAccountEntrySource run), and environment.
 *
 * Exported so other account trackers (e.g. the super-checkout telemetry
 * bridge) can attach the same attribution. Deliberately excludes `tags` —
 * those differ per caller.
 */
export function getAccountSessionMetadata(): Record<string, unknown> {
  return {
    account_session_id: getSessionValue(SESSION_ID_KEY),
    account_user_id: getSessionValue(USER_ID_KEY),
    account_org_id: getSessionValue(ORG_ID_KEY),
    account_entry_source: getSessionValue(ENTRY_SOURCE_KEY),
    account_entry_source_type: getSessionValue(ENTRY_SOURCE_TYPE_KEY),
    environment: process.env.NEXT_PUBLIC_SAM_ENV || "local",
  };
}

/**
 * Track an account interaction event.
 *
 * Automatically attaches session ID, userId, orgId, and environment.
 * PII fields in metadata are anonymized before sending.
 *
 * @param action - Event name in format "{group}:{event}" (e.g. "auth:dob_verified")
 * @param metadata - Additional event-specific data
 */
export function trackAccountEvent(
  action: string,
  metadata: Record<string, unknown> = {},
): void {
  trackEvent({
    category: "interaction",
    context: "account",
    action,
    metadata: {
      ...getAccountSessionMetadata(),
      tags: ["ACCOUNT"],
      ...anonymizeAccountData(metadata),
    },
  });
}
